import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PostCard from '@/components/base/PostCard';
import SubscribeForm from '@/components/feature/SubscribeForm';
import useSeo from '@/hooks/useSeo';
import { posts } from '@/mocks/posts';

export default function PostPage() {
  const { id = '' } = useParams();
  const post = posts.find((p) => p.id === id);

  useSeo({
    title: post ? `${post.title} | 18爆料网` : '内容未找到 | 18爆料网',
    description: post
      ? post.summary.slice(0, 150)
      : '这篇爆料可能已被撤下，返回首页查看 18爆料网今日更新的内容。',
    keywords: post
      ? `18爆料网,${post.categoryLabel},${post.tags.join(',')},18爆料网今日爆料`
      : '18爆料网,18爆料网今日更新',
    path: `/post/${id}`,
    type: 'article',
    jsonLd: post
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: post.title,
            description: post.summary,
            inLanguage: 'zh-CN',
            articleSection: post.categoryLabel,
            keywords: post.tags.join(','),
            datePublished: post.publishedAt,
            author: { '@type': 'Organization', name: post.author },
            publisher: { '@type': 'Organization', name: '18爆料网' },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
              { '@type': 'ListItem', position: 2, name: post.categoryLabel, item: `/category/${post.categorySlug}` },
              { '@type': 'ListItem', position: 3, name: post.title },
            ],
          },
        ]
      : [],
  });

  if (!post) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="flex w-full flex-col items-center justify-center px-4 py-24 text-center">
          <span className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
            <i className="ri-file-unknow-line" aria-hidden="true"></i>
          </span>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">这篇爆料找不到了</h1>
          <p className="mt-3 text-sm text-foreground-600">内容可能已被撤下或调整，去首页看看今天的新瓜。</p>
          <Link
            to="/"
            className="mt-6 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const related = posts.filter((p) => p.categorySlug === post.categorySlug && p.id !== post.id).slice(0, 3);
  const more = posts.filter((p) => p.id !== post.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="w-full">
        <Breadcrumb
          items={[{ label: post.categoryLabel, to: `/category/${post.categorySlug}` }, { label: post.title }]}
        />

        <article className="w-full px-4 pb-12 md:px-8">
          <header className="mb-8">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Link
                to={`/category/${post.categorySlug}`}
                className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
              >
                {post.categoryLabel}
              </Link>
              {post.hot && (
                <span className="flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                  <i className="ri-fire-fill" aria-hidden="true"></i>
                  热门爆料
                </span>
              )}
            </div>
            <h1 className="font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {post.title}
            </h1>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-foreground-600">{post.summary}</p>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-3-line" aria-hidden="true"></i>
                {post.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" aria-hidden="true"></i>
                {post.publishedAt}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" aria-hidden="true"></i>
                {post.views.toLocaleString('zh-CN')} 次围观
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" aria-hidden="true"></i>
                {post.readMinutes} 分钟读完
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
                热度 {post.heat}
              </span>
            </div>
          </header>

          <div className="mb-8 h-[260px] w-full overflow-hidden rounded-lg md:h-[420px]">
            <img src={post.cover} alt={post.coverAlt} className="h-full w-full object-cover object-top" />
          </div>

          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_300px]">
            <div className="min-w-0">
              {post.sections.map((section, index) => (
                <section key={section.heading} id={`section-${index + 1}`} className="mb-9 scroll-mt-20">
                  <h2 className="mb-3 flex items-start gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-accent-500 text-xs font-bold text-background-50">
                      {index + 1}
                    </span>
                    {section.heading}
                  </h2>
                  <div className="space-y-4 text-base leading-loose text-foreground-700">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph.slice(0, 24)}>{paragraph}</p>
                    ))}
                  </div>
                </section>
              ))}

              <section className="mt-10 rounded-lg border border-background-200 bg-background-100 p-6">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-git-commit-line text-primary-500" aria-hidden="true"></i>
                  事件时间线
                </h2>
                <ol className="relative border-l border-background-300 pl-6">
                  {post.timeline.map((item) => (
                    <li key={item.time} className="mb-5 last:mb-0">
                      <span className="absolute -left-[7px] mt-1.5 h-3 w-3 rounded-full border-2 border-background-100 bg-primary-500"></span>
                      <p className="text-xs font-medium text-primary-600">{item.time}</p>
                      <p className="mt-1 text-sm leading-relaxed text-foreground-700">{item.text}</p>
                    </li>
                  ))}
                </ol>
              </section>

              <div className="mt-8 flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-foreground-700">相关标签：</span>
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <aside className="flex flex-col gap-6 lg:sticky lg:top-20 lg:self-start" aria-label="侧边栏">
              <nav className="rounded-lg border border-background-200 bg-background-50 p-5" aria-label="内容目录">
                <h2 className="mb-3 font-heading text-base font-bold text-foreground-950">内容目录</h2>
                <ul className="space-y-1">
                  {post.sections.map((section, index) => (
                    <li key={section.heading}>
                      <a
                        href={`#section-${index + 1}`}
                        className="block rounded-md px-3 py-2 text-sm text-foreground-600 transition-colors hover:bg-background-100 hover:text-primary-600"
                      >
                        {index + 1}. {section.heading}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              {related.length > 0 && (
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="mb-2 font-heading text-base font-bold text-foreground-950">同栏目推荐</h2>
                  <div className="divide-y divide-background-200">
                    {related.map((item) => (
                      <PostCard key={item.id} post={item} variant="compact" />
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>

          <section className="mt-14" aria-labelledby="more-heading">
            <h2 id="more-heading" className="mb-5 font-heading text-2xl font-bold text-foreground-950">
              你可能还想看
            </h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {more.map((item) => (
                <PostCard key={item.id} post={item} variant="grid" />
              ))}
            </div>
          </section>

          <section className="mt-14 rounded-lg bg-background-100 p-6 md:p-8" aria-labelledby="post-subscribe">
            <h2 id="post-subscribe" className="mb-5 font-heading text-xl font-bold text-foreground-950">
              跟进这条事件 · 有新进展第一时间通知你
            </h2>
            <SubscribeForm />
          </section>
        </article>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}